/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import {
  PaymentMethodCode,
  type UserExtra,
  type PaymentMethod,
} from '@ahm/api-wrappers-core';
import {
  WEBINAPP_MOMO_ID,
  WEBINAPP_MOMO_MINIAPP,
  WEBINAPP_SHOPEEPAY_ID,
  WEBINAPP_VIETTELPAY_ID,
  WEBINAPP_ZALOPAY_ID,
  WEBINAPP_ZALO_ID,
  useAuth,
} from '@ahm/react-auth';
import { openContextModal } from '@mantine/modals';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { ModalName } from '@/components/modals';
import { ROUTES } from '@/config/router';
import { useGetOrderList } from '@/features/booking/api/order/use-get-order-list';
import { useGetPaymentMethods } from '@/features/booking/api/payment';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

const paramOrderList = { total: true, offset: 0, count: 30 };

const WEBINAPP_METHOD_MAP: Record<string, string> = {
  [WEBINAPP_MOMO_ID]: PaymentMethodCode.Momo.toLowerCase(),
  [WEBINAPP_MOMO_MINIAPP]: PaymentMethodCode.Momo.toLowerCase(),
  [WEBINAPP_ZALO_ID]: PaymentMethodCode.Zalopay.toLowerCase(),
  [WEBINAPP_ZALOPAY_ID]: PaymentMethodCode.Zalopay.toLowerCase(),
  [WEBINAPP_VIETTELPAY_ID]: PaymentMethodCode.Viettelpay.toLowerCase(),
  [WEBINAPP_SHOPEEPAY_ID]: PaymentMethodCode.Shopeepay.toLowerCase(),
};

const NEED_LINK_METHODS = [
  PaymentMethodCode.Zalopay.toLowerCase(),
  PaymentMethodCode.Shopeepay.toLowerCase(),
];

export function usePaymentMethodHook() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const orderId = searchParams.get('order_id') ?? '';

  const {
    estimatedData,
    selectedService,
    paymentMethod: selectedPaymentMethod,
    setPaymentMethod: setSelectedPaymentMethod,
  } = useBookingStore((state) => state);

  const { session, webInAppConfig } = useAuth();
  const user = session?.user;

  const { data: orderList } = useGetOrderList(paramOrderList);

  const estimatedTotalPrice = estimatedData?.data?.total_price;
  const { data: paymentMethodList } = useGetPaymentMethods(
    estimatedTotalPrice !== undefined
      ? { amount: Number(estimatedTotalPrice) }
      : {},
    { enabled: !!user }
  );

  const validPaymentMethodList = useMemo(() => {
    if (!paymentMethodList?.length) return [];

    return paymentMethodList
      .filter((item) =>
        selectedService?._id
          ? !item.disabled_for_services?.includes(selectedService._id)
          : true
      )
      .filter((item) => !item.error_message_vi_vn && !item.error_message_en_us);
  }, [paymentMethodList, selectedService]);

  const available = validPaymentMethodList.filter(
    (m) => !m.error_message_vi_vn && !m.error_message_en_us
  );

  const paymentPayloadData = useMemo(() => {
    if (!selectedPaymentMethod) return;
    const { method_id, method_type, code } = selectedPaymentMethod;
    const paymentMethod =
      method_type === PaymentMethodCode.Card.toLowerCase()
        ? PaymentMethodCode.Card.toLowerCase()
        : code;

    if (pathname === ROUTES.nationwide) {
      return {
        payment_method_id: method_id,
        payment_method: paymentMethod,
      };
    }

    return {
      method_id,
      method_type,
      payment_method: paymentMethod,
    };
  }, [selectedPaymentMethod, pathname, selectedService]);

  const handleSetPaymentMethod = useCallback(
    (data?: PaymentMethod) => {
      const fallback = validPaymentMethodList[0];
      if (!data?.method_id) return setSelectedPaymentMethod(fallback);

      const match = validPaymentMethodList.find(
        (item) => item.method_id === data.method_id
      );
      setSelectedPaymentMethod(match ?? fallback);
    },
    [validPaymentMethodList, setSelectedPaymentMethod]
  );

  const isNotEnoughBalance = useMemo(() => {
    if (!user || !estimatedData?.data) return false;

    const balance = user.main_account.VND;
    const total = estimatedData.data.total_price;
    const balanceMethod = validPaymentMethodList.find(
      (item) => item.code === 'balance'
    );

    return (
      selectedPaymentMethod?.method_id === balanceMethod?.method_id &&
      balance < total &&
      user.postpaid !== 1
    );
  }, [estimatedData, user, selectedPaymentMethod, validPaymentMethodList]);

  const needLinkMethod = useCallback((method: PaymentMethod) => {
    return method.is_support_tokenize && !method?.extra_data?.is_linked;
  }, []);

  const showNotEnoughBalanceModal = () => {
    openContextModal({
      id: ModalName.NotEnoughBalance,
      modal: ModalName.NotEnoughBalance,
      innerProps: {},
      styles: { body: { padding: 0 } },
      centered: true,
      closeOnClickOutside: false,
      closeOnEscape: false,
      withCloseButton: false,
    });
  };

  const initPaymentMethod = useCallback(() => {
    if (
      !available.length ||
      !user ||
      orderId ||
      searchParams.get('paymentAction')
    ) {
      return;
    }

    // Ưu tiên chọn web-in-app
    const code = webInAppConfig?.id && WEBINAPP_METHOD_MAP[webInAppConfig.id];
    if (code) {
      const method = available.find((m) => m.code === code);
      if (method) return handleSetPaymentMethod(method);
    }

    const isCurrentMethodValid = selectedPaymentMethod
      ? available.some((m) => m.method_id === selectedPaymentMethod.method_id)
      : false;

    if (!isCurrentMethodValid) {
      const lastOrder = orderList?.orders?.[0];
      const lastOrderMethod = lastOrder
        ? available.find(
            (m) => m.method_id === lastOrder.payment_details.method_id
          )
        : undefined;
      const supportTokenizeMethod = available.find(
        (m) => m.is_support_tokenize && m.extra_data?.is_linked
      );

      const fallback =
        available.find((m) => m.is_default) ||
        (lastOrderMethod?.is_support_tokenize &&
          lastOrderMethod?.extra_data?.is_linked &&
          lastOrderMethod) ||
        supportTokenizeMethod ||
        lastOrderMethod ||
        available.find(
          (m) => m.code === PaymentMethodCode.Cash.toLowerCase()
        ) ||
        available[0];

      if (fallback) setSelectedPaymentMethod(fallback);
    }
  }, [
    available,
    user,
    orderId,
    searchParams,
    webInAppConfig?.id,
    selectedPaymentMethod,
    handleSetPaymentMethod,
    setSelectedPaymentMethod,
    orderList?.orders,
  ]);

  return {
    initPaymentMethod,
    validPaymentMethodList,
    paymentPayloadData,
    isNotEnoughBalance,
    showNotEnoughBalanceModal,
    paymentMethodList,
    handleSetPaymentMethod,
    needLinkMethod,
  };
}
